import React, { useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import PropTypes from 'prop-types';
const _borderColor = 'rgba(255, 255, 255, 0.15)';
const ROW_PER_PAGE = [10, 25, 100];

const lightThemeBold = {
  '& thead tr th': {
    color: 'rgba(0, 0, 0, 0.80);',
    fontWeight: 700,
    borderColor: 'transparent',
    background: 'transparent',
    fontSize: '12px',
  },
  '& th': {
    pb: 0,
  },
  '& td': {
    border: '2px solid #EFEFEF',
    borderStyle: 'solid none',
    background: '#FFF',
  },
  '& td:last-child': {
    borderRightStyle: 'solid',
    borderBottomRightRadius: '12px',
    borderTopRightRadius: '12px',
  },
  '& td:first-of-type': {
    borderLeftStyle: 'solid',
    borderBottomLeftRadius: '12px',
    borderTopLeftRadius: '12px',
  },
  boxShadow: 'none',
};

const lightTheme = {
  bgcolor: '#fff',
  '& thead tr th': {
    color: 'rgba(0, 0, 0, 0.60);',
    fontWeight: 700,
    borderColor: 'transparent',
    background: 'transparent',
  },
  '& th': {
    pb: 0,
  },
  '& td': {
    border: '2px solid #EFEFEF',
    borderStyle: 'solid none',
    background: '#F8F8F8',
  },
  '& td:last-child': {
    borderRightStyle: 'solid',
    borderBottomRightRadius: '12px',
    borderTopRightRadius: '12px',
  },
  '& td:first-of-type': {
    borderLeftStyle: 'solid',
    borderBottomLeftRadius: '12px',
    borderTopLeftRadius: '12px',
  },
  boxShadow: 'none',
};

const THEME = {
  lightBold: lightThemeBold,
  light: lightTheme,
};

const THEME_NAVIGATION = {
  dark: {
    '& p': {
      color: 'white',
    },
    '& svg': {
      color: 'white',
    },
    '& .MuiSelect-select': {
      color: 'white',
    },
  },
  light: {
    '& p': {
      color: '#000',
    },
    '& svg': {
      color: '#000',
    },
    '& .MuiSelect-select': {
      color: '#000',
    },
  },
};

/**
 * Renders a table component with customizable columns and rows.
 *
 * @param {array} columns - An array of objects representing the table columns.
 * @param {array} rows - An array of objects representing the table rows.
 * @param {object} sx - Additional styles for the table component.
 * @param {string} borderColor - The color of the table border.
 * @param {number} maxHeight - The maximum height of the table.
 * @param {boolean} resetPage - Whether to reset the page when the component re-renders.
 * @param {string} theme - The theme of the table component.
 * @param {string} emptyMessage - The message to display when there are no rows.
 * @param {function} onClickRow - The function to call when a row is clicked.
 * @param {object} other - Additional props for the table component.
 * @return {ReactElement} The rendered table component.
 */
export default function TableComponent({
  columns = [],
  rows = [],
  sx = {},
  borderColor = _borderColor,
  maxHeight,
  resetPage,
  theme = 'light',
  emptyMessage,
  loading,
  loadingComponent,
  onClickRow = null,
  queryTable,
  page,
  setPage,
  setRowsPerPage,
  rowsPerPage,
  total,
  hidePagination = false,
  label_sx = {},
  ...other
}) {
  useEffect(() => {
    setPage && setPage(0);
    setRowsPerPage && setRowsPerPage(ROW_PER_PAGE[0]);
  }, [resetPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
  };

  return (
    <Paper
      sx={{
        width: '100%',
        overflow: 'hidden',
        ...(THEME[theme] || THEME.dark),
        ...sx,
      }}
      {...other}
    >
      <TableContainer sx={{ maxHeight: maxHeight ?? 'auto' }}>
        <Table
          stickyHeader
          aria-label="sticky table"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: '0px 4px',
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((column, i) => (
                <TableCell
                  key={column.id + i + column.label}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  sx={label_sx}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              return (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={(row._id || 0) + index}
                  onClick={(e) => {
                    if (onClickRow) {
                      onClickRow(row);
                    }
                  }}
                  sx={{
                    cursor: onClickRow && 'pointer',
                  }}
                >
                  {columns.map((column, i) => {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id + i} align={column.align}>
                        {column.render
                          ? column.render(row)
                          : column.format && typeof value === 'number'
                            ? column.format(value)
                            : value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
            {loading ? (
              <>
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    {loadingComponent}
                  </TableCell>
                </TableRow>
              </>
            ) : (
              rows.length === 0 &&
              emptyMessage && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!hidePagination && (
        <TablePagination
          rowsPerPageOptions={ROW_PER_PAGE}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ...(THEME_NAVIGATION[theme] || THEME.dark),
          }}
        />
      )}
    </Paper>
  );
}
let column = PropTypes.shape({
  id: PropTypes.string,
  label: PropTypes.string,
  minWidth: PropTypes.number,
  align: PropTypes.string,
  format: PropTypes.func,
});
TableComponent.propTypes = {
  columns: PropTypes.arrayOf(column),
  rows: PropTypes.arrayOf(PropTypes.object),
};
